import { useApiMutation } from "@gladis/api-client";
import { formatEur, type MemberDto } from "@gladis/shared";
import { PersonLabel, Table } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { useState, type ReactNode } from "react";

import { SAVINGS_KEY, useSavingsAccounts } from "./AccountList.js";

/**
 * The archived accounts, hidden behind a disclosure (FR-037, FR-037a).
 *
 * A DISCLOSURE, NOT A FILTER. Showing this list changes nothing above it — not the combined total,
 * not the active accounts, not the chart. That is why the server returns both lists in one
 * response: it makes the guarantee structural instead of a rule somebody has to keep.
 *
 * No separate route, deliberately. Recovery belongs where the mistake was made, and archiving is
 * too rare to earn a page of its own.
 *
 * The balance shown is what the account held WHEN IT WAS ARCHIVED, not zero and not today's. Zero
 * would read as "this account was emptied", which is a different and alarming claim.
 */
export function ArchivedAccounts({
  onOpen,
  members = [],
}: {
  onOpen?: (accountId: string) => void;
  /** The household's members, for their faces (020). Passed in, as in `AccountList`. */
  members?: readonly MemberDto[];
}): ReactNode {
  const t = useT();
  const [shown, setShown] = useState(false);
  const accounts = useSavingsAccounts();

  const unarchive = useApiMutation<void, { accountId: string }>(
    (client, input) => client.post<void>(`/savings/accounts/${input.accountId}/unarchive`, {}),
    { invalidates: [SAVINGS_KEY] },
  );

  const archived = accounts.data?.archived ?? [];
  if (archived.length === 0) return null;

  return (
    <section className="mt-10">
      <button type="button" className="text-sm underline" onClick={() => setShown(!shown)}>
        {shown ? "Hide" : "Show"} archived accounts ({archived.length})
      </button>

      {shown ? (
        <div className="mt-4">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>{t("savings.list.colAccount")}</Table.HeaderCell>
                <Table.HeaderCell>{t("savings.list.colHeldBy")}</Table.HeaderCell>
                <Table.HeaderCell numeric>
                  {t("savings.archived.colBalanceWhenArchived")}
                </Table.HeaderCell>
                <Table.HeaderCell> </Table.HeaderCell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {archived.map((account) => (
                <Table.Row key={account.id}>
                  <Table.Cell>
                    {onOpen ? (
                      <button
                        type="button"
                        className="underline"
                        onClick={() => onOpen(account.id)}
                      >
                        {account.name}
                      </button>
                    ) : (
                      account.name
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <PersonLabel
                      label={account.ownerLabel ?? t("person.joint")}
                      isPerson={account.ownerLabel !== null}
                      avatarUrl={members.find((m) => m.userId === account.ownerId)?.avatarUrl}
                      group={members.map((m) => ({ name: m.displayName, avatarUrl: m.avatarUrl }))}
                    />
                  </Table.Cell>
                  <Table.Cell numeric>{formatEur(account.currentBalance)}</Table.Cell>
                  {/*
                   * A DIRECT BUTTON, AND DELIBERATELY NOT AN `ActionMenu` — which the active list next to
                   * it gained on 2026-08-14. An archived account offers exactly one action, and a one-item
                   * menu spends a click on a choice that does not exist. The mortgage row on the expenses
                   * page is the precedent and keeps its direct control for the same reason.
                   *
                   * The asymmetry is the point: two triggers in one table means two actions somewhere.
                   */}
                  <Table.Cell>
                    <button
                      type="button"
                      className="underline"
                      disabled={unarchive.isPending}
                      onClick={() => unarchive.mutate({ accountId: account.id })}
                    >
                      {t("savings.archived.restore")}
                    </button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : null}
    </section>
  );
}
